import api from './api';

export interface ServicePrice {
  id: number;
  duration_days: number;
  price: number;
  currency: string;
}

export interface Service {
  id: number;
  service_type: 'cv_database_access' | 'job_promotion' | 'additional_job_slot';
  name: string;
  description: string;
  is_active: boolean;
  prices: ServicePrice[];
}

export interface ServicePurchase {
  id: number;
  user_id: number;
  service_id: number;
  service_name?: string;
  service_type?: string;
  job_id: number | null;
  job_title?: string;
  duration_days: number;
  price_paid: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method: string;
  starts_at: string | null;
  expires_at: string | null;
  created_at: string;
}

const serviceManagementService = {
  // Get all services with prices
  getAllServices: async (): Promise<Service[]> => {
    const response = await api.get('/services');
    return response.data;
  },

  // Purchase a service
  purchaseService: async (servicePriceId: number, jobId?: number): Promise<any> => {
    const response = await api.post('/services/purchase', {
      service_price_id: servicePriceId,
      job_id: jobId
    });
    return response.data;
  },

  // Get active purchases
  getActivePurchases: async (): Promise<ServicePurchase[]> => {
    const response = await api.get('/services/active');
    return response.data;
  },

  // Get purchase history
  getPurchaseHistory: async (): Promise<ServicePurchase[]> => {
    const response = await api.get('/services/history');
    return response.data;
  },

  // Check CV access
  checkCVAccess: async (): Promise<boolean> => {
    const response = await api.get('/services/cv-access');
    return response.data.has_access;
  },

  // Get additional job slots
  getAdditionalSlots: async (): Promise<number> => {
    const response = await api.get('/services/additional-slots');
    return response.data.additional_slots;
  },

  // Admin: Update service price
  updateServicePrice: async (priceId: number, price: number): Promise<any> => {
    const response = await api.put(`/services/admin/prices/${priceId}`, { price });
    return response.data;
  },

  // Admin: Create service price
  createServicePrice: async (data: {
    service_id: number;
    duration_days: number;
    price: number;
    currency: string;
  }): Promise<any> => {
    const response = await api.post('/services/admin/prices', data);
    return response.data;
  },

  // Admin: Delete service price
  deleteServicePrice: async (priceId: number): Promise<any> => {
    const response = await api.delete(`/services/admin/prices/${priceId}`);
    return response.data;
  },

  createPayment: async (params: {
    servicePriceId?: number;
    jobId?: number;
    cvId?: number;
    amount?: number;
    currency?: string;
    durationDays?: number;
    description?: string;
    discountCode?: string;
  }): Promise<{ success: boolean; purchase_id: number; payment_url: string }> => {
    const response = await api.post('/payments/create', {
      service_price_id: params.servicePriceId,
      job_id: params.jobId,
      cv_id: params.cvId,
      amount: params.amount,
      currency: params.currency,
      duration_days: params.durationDays,
      description: params.description,
      discount_code: params.discountCode || undefined,
    });
    return response.data;
  },

  getPaymentStatus: async (purchaseId: number): Promise<{ success: boolean; purchase_id: number; status: string; service_name?: string; price_paid?: number; currency?: string }> => {
    const response = await api.get(`/payments/status/${purchaseId}`);
    return response.data;
  },
};

export default serviceManagementService;

